import type { IsoDateTime, Uuid } from '@/shared/types/domain'

/**
 * Accesos mixtos (plan 2.5).
 *
 * `stored` = cifrado en la app. `vaulted` = SOLO una referencia a 1Password /
 * Bitwarden, sin secreto de este lado. Las categorias `prod` y `payments`
 * estan obligadas a ser `vaulted` por un CHECK de la base: acá la interfaz lo
 * explica ANTES de que alguien intente, pero la politica vive en Postgres.
 */
export type AccessCategory =
  'panel' | 'sandbox' | 'docs' | 'staging' | 'prod' | 'payments' | 'other'

export type AccessKind = 'stored' | 'vaulted'

/**
 * Lo que devuelve `GET /companies/{id}/accesses`: **metadata, jamás el
 * secreto**. El unico camino al secreto es `POST /accesses/{id}/reveal`.
 */
export interface CompanyAccess {
  id: Uuid
  company_id: Uuid
  label: string
  category: AccessCategory
  kind: AccessKind
  url: string | null
  username: string | null
  vault_provider: string | null
  /** Lo que guarda el backend: el ID del item en el gestor, no una URL. */
  vault_item_id: string | null
  /** Derivado de `vault_provider` + `vault_item_id`. `null` si no se sabe armar. */
  vault_item_url: string | null
  /** `notes_internal` en el backend. */
  notes: string | null
  /** Hay secreto guardado y revelable. Nunca trae el secreto en sí. */
  has_secret: boolean
}

/** Respuesta de `POST /accesses/{id}/reveal`. Auditada, con rate-limit 20/h. */
export interface RevealedSecret {
  access_id: Uuid
  username: string | null
  secret: string
  revealed_at: IsoDateTime
}

/**
 * Alta de un acceso. Va a `POST /accesses` con `company_id` en el cuerpo — no
 * existe `POST /companies/{id}/accesses`, que es lo que el frontend llamaba.
 */
export interface AccessCreateInput {
  label: string
  category: AccessCategory
  kind: AccessKind
  url?: string | null
  username?: string | null
  secret?: string | null
  vault_provider?: string | null
  vault_item_id?: string | null
  notes?: string | null
}

/** Categorías que el backend obliga a `vaulted`. */
export const CRITICAL_CATEGORIES: readonly AccessCategory[] = ['prod', 'payments']

export function isCriticalCategory(category: AccessCategory): boolean {
  return CRITICAL_CATEGORIES.includes(category)
}

/**
 * La categoría no es dato del usuario: la lista la escribimos nosotros, así que
 * se traduce. Cada entrada es la CLAVE del catálogo y se usa `t(CATEGORY_KEY[c])`.
 */
export const CATEGORY_KEY: Record<AccessCategory, string> = {
  panel: 'accesses.category.panel',
  sandbox: 'accesses.category.sandbox',
  docs: 'accesses.category.docs',
  staging: 'accesses.category.staging',
  prod: 'accesses.category.prod',
  payments: 'accesses.category.payments',
  other: 'accesses.category.other',
}

/** Orden de los grupos: lo crítico arriba, porque es lo que se busca apurado. */
export const CATEGORY_ORDER: readonly AccessCategory[] = [
  'prod',
  'payments',
  'staging',
  'panel',
  'sandbox',
  'docs',
  'other',
]

/**
 * El nombre de marca de cada gestor de credenciales.
 *
 * `vault_provider` es un **slug** de base —hay un CHECK que lo exige en
 * minúscula (`app/modules/accesses/models.py`)— y la pantalla lo estaba
 * mostrando crudo: el botón decía **"Abrir en 1password"**, con la marca mal
 * escrita. El QA funcional lo anotó como detalle (2.5), y lo es; pero es el
 * nombre de un producto de otro y aparece en el vault, que es la pantalla donde
 * más importa que todo parezca cuidado.
 *
 * No se traduce: un nombre propio se escribe igual en los tres idiomas. Por eso
 * es un mapa acá y no una clave de catálogo.
 *
 * Un proveedor que no esté en el mapa sale tal como vino: mejor un slug que un
 * hueco, y el CHECK de la base ya limita qué valores pueden llegar.
 */
const NOMBRE_DE_GESTOR: Record<string, string> = {
  '1password': '1Password',
  bitwarden: 'Bitwarden',
}

export function vaultProviderName(provider: string | null | undefined): string {
  if (!provider) return NOMBRE_DE_GESTOR['1password']!
  return NOMBRE_DE_GESTOR[provider.toLowerCase()] ?? provider
}
